import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { fetchAccountDetails, fetchOpenTrades } from '../api/api';
import API_BASE_URL, { WS_BASE_URL } from '../api/config';

const AccountDataContext = createContext();
const ACCOUNT_WS_RECONNECT_INITIAL_MS = 2_000;
const ACCOUNT_WS_RECONNECT_MAX_MS = 30_000;
const ACCOUNT_REST_PRIME_DEDUPE_MS = 15_000;

export const useAccountData = () => useContext(AccountDataContext);

/**
 * Normalize a position object so it always carries the field names that
 * OpenPositions.jsx (and other UI components) expect — i.e. the same
 * shape the WebSocket `account_update` messages use.
 *
 * The HTTP snapshot endpoints (`/accounts/details/`, `/trades/open-positions/`)
 * return slightly different key names (e.g. `open_price` vs `price_open`,
 * `stop_loss` vs `sl`, `take_profit` / `profit_target` vs `tp`, `instrument`
 * vs `symbol`, `lot_size` vs `volume`, `entry_price` vs `price_open`).
 *
 * We keep every original key intact and **add** the canonical aliases so the
 * UI never sees undefined for a field that was present under a different name.
 */
const normalizePosition = (pos) => {
    if (!pos || typeof pos !== 'object') return pos;
    const p = { ...pos };

    // ticket / position_id
    if (p.ticket == null && p.position_id != null) p.ticket = p.position_id;

    // symbol / instrument
    if (p.symbol == null && p.instrument != null) p.symbol = p.instrument;

    // volume / lot_size
    if (p.volume == null && p.lot_size != null) p.volume = p.lot_size;

    // price_open / open_price / entry_price
    if (p.price_open == null) {
        p.price_open = p.open_price ?? p.entry_price ?? null;
    }

    // price_current / current_price
    if (p.price_current == null && p.current_price != null) p.price_current = p.current_price;

    // sl / stop_loss
    if (p.sl == null && p.stop_loss != null) p.sl = p.stop_loss;

    // tp / take_profit / profit_target
    if (p.tp == null) {
        p.tp = p.take_profit ?? p.profit_target ?? null;
    }

    // Open time is optional, but chart trade overlays can use it to place
    // active position markers on the entry candle.
    if (p.open_time == null) {
        p.open_time = p.time ?? p.created_at ?? p.createdAt ?? p.opened_at ?? p.openedAt ?? null;
    }

    // Ensure direction is uppercase ("BUY" / "SELL") for consistent UI styling
    if (typeof p.direction === 'string') {
        p.direction = p.direction.toUpperCase();
    }

    return p;
};

const normalizePositions = (list) => {
    if (!Array.isArray(list)) return list;
    return list.map(normalizePosition);
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

export const AccountDataProvider = ({ children }) => {
    const [balance, setBalance] = useState(null);
    const [equity, setEquity] = useState(null);
    const [margin, setMargin] = useState(null);
    const [freeMargin, setFreeMargin] = useState(null);
    const [marginLevel, setMarginLevel] = useState(null);
    const [cashAvailable, setCashAvailable] = useState(null);
    const [floatingPnl, setFloatingPnl] = useState(null);
    const [currency, setCurrency] = useState(null);
    const [openPositions, setOpenPositions] = useState([]);
    const [pendingOrders, setPendingOrders] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [connectedAccountId, setConnectedAccountId] = useState(null);
    const wsRef = useRef(null);
    const socketsRef = useRef(new Map());
    const heartbeatTimersRef = useRef(new Map());
    const reconnectTimersRef = useRef(new Map());
    const reconnectDelaysRef = useRef(new Map());
    const accountSnapshotsRef = useRef(new Map());
    const snapshotRequestsRef = useRef(new Map());
    const snapshotPrimeTimesRef = useRef(new Map());
    const selectedAccountRef = useRef(null);
    const connectReqRef = useRef(0); // used to ignore stale HTTP responses
    const connectedAccountRef = useRef(null); // track which account is currently connected

    const clearReconnectTimer = useCallback((accountId) => {
        const timer = reconnectTimersRef.current.get(accountId);
        if (timer) {
            clearTimeout(timer);
            reconnectTimersRef.current.delete(accountId);
        }
    }, []);

    const resetVisibleState = useCallback(() => {
        setBalance(null);
        setEquity(null);
        setMargin(null);
        setFreeMargin(null);
        setMarginLevel(null);
        setCashAvailable(null);
        setFloatingPnl(null);
        setCurrency(null);
        setOpenPositions([]);
        setPendingOrders([]);
    }, []);

    const applySnapshot = useCallback((snapshot = {}) => {
        if ('balance' in snapshot) setBalance(snapshot.balance);
        if ('equity' in snapshot) setEquity(snapshot.equity);
        if ('margin' in snapshot) setMargin(snapshot.margin);
        if ('freeMargin' in snapshot) setFreeMargin(snapshot.freeMargin);
        if ('marginLevel' in snapshot) setMarginLevel(snapshot.marginLevel);
        if ('cashAvailable' in snapshot) setCashAvailable(snapshot.cashAvailable);
        if ('floatingPnl' in snapshot) setFloatingPnl(snapshot.floatingPnl);
        if ('currency' in snapshot) setCurrency(snapshot.currency);
        if ('openPositions' in snapshot) {
            const nextOpenPositions = normalizePositions(snapshot.openPositions || []);
            setOpenPositions((prev) => (
                JSON.stringify(prev) !== JSON.stringify(nextOpenPositions) ? nextOpenPositions : prev
            ));
        }
        if ('pendingOrders' in snapshot) {
            const nextPendingOrders = snapshot.pendingOrders || [];
            setPendingOrders((prev) => (
                JSON.stringify(prev) !== JSON.stringify(nextPendingOrders) ? nextPendingOrders : prev
            ));
        }
    }, []);

    const cacheAndApplySnapshot = useCallback((accountId, patch) => {
        if (!accountId || !patch) return;
        const current = accountSnapshotsRef.current.get(accountId) || {};
        const next = { ...current, ...patch };
        accountSnapshotsRef.current.set(accountId, next);
        if (selectedAccountRef.current === accountId) {
            applySnapshot(next);
        }
    }, [applySnapshot]);

    const closeAllSockets = useCallback(() => {
        heartbeatTimersRef.current.forEach((timer) => clearInterval(timer));
        heartbeatTimersRef.current.clear();
        reconnectTimersRef.current.forEach((timer) => clearTimeout(timer));
        reconnectTimersRef.current.clear();
        reconnectDelaysRef.current.clear();
        wsRef.current = null;
        connectedAccountRef.current = null;
        selectedAccountRef.current = null;
        socketsRef.current.forEach((ws) => {
            try {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ action: 'unsubscribe' }));
                }
            } catch (_) {}
            try { ws.close(); } catch (_) {}
        });
        socketsRef.current.clear();
    }, []);

    const connect = useCallback((accountId, options = {}) => {
        const { primeSnapshot = true, resetOnNoCache = true, forcePrimeSnapshot = false } = options;

        if (!accountId) {
            selectedAccountRef.current = null;
            connectedAccountRef.current = null;
            setConnectedAccountId(null);
            resetVisibleState();
            setIsConnected(false);
            return;
        }

        clearReconnectTimer(accountId);
        selectedAccountRef.current = accountId;
        connectedAccountRef.current = accountId;
        setConnectedAccountId(accountId);

        const cached = accountSnapshotsRef.current.get(accountId);
        if (cached) {
            applySnapshot(cached);
        } else if (resetOnNoCache) {
            resetVisibleState();
        }

        const existingSocket = socketsRef.current.get(accountId);
        if (
            existingSocket &&
            (existingSocket.readyState === WebSocket.OPEN || existingSocket.readyState === WebSocket.CONNECTING)
        ) {
            wsRef.current = existingSocket;
            setIsConnected(existingSocket.readyState === WebSocket.OPEN);
            return;
        }

        const now = Date.now();
        const lastPrimeAt = snapshotPrimeTimesRef.current.get(accountId) || 0;
        const hasPrimeInFlight = snapshotRequestsRef.current.has(accountId);
        const shouldPrimeSnapshot = (
            primeSnapshot &&
            !hasPrimeInFlight &&
            (forcePrimeSnapshot || !cached || now - lastPrimeAt >= ACCOUNT_REST_PRIME_DEDUPE_MS)
        );

        // Fire one deduped HTTP snapshot to prime UI while WS connects.
        if (shouldPrimeSnapshot) {
            snapshotPrimeTimesRef.current.set(accountId, now);
            connectReqRef.current += 1;
            const reqId = connectReqRef.current;
            const request = (async () => {
                try {
                    let details = null;
                    let detailsWarmingUp = false;
                    try {
                        details = await fetchAccountDetails(accountId);
                    } catch (_) {
                        details = null;
                    }
                    // Ignore if a newer connect was initiated
                    if (connectReqRef.current !== reqId) return;

                    const snapshot = {};
                    let hasOpenPositionsSnapshot = false;
                    if (details) {
                        const d = details;
                        detailsWarmingUp = d.live_status === 'warming_up' || d.retryable === true;
                        if (typeof d.balance !== 'undefined') {
                            const b = Number(d.balance);
                            if (!isNaN(b)) snapshot.balance = b;
                        }
                        if (typeof d.equity !== 'undefined') {
                            const e = Number(d.equity);
                            if (!isNaN(e)) snapshot.equity = e;
                        }
                        if (typeof d.margin !== 'undefined') {
                            const m = Number(d.margin);
                            if (!isNaN(m)) snapshot.margin = m;
                        }
                        if (typeof d.free_margin !== 'undefined') {
                            const fm = Number(d.free_margin);
                            if (!isNaN(fm)) snapshot.freeMargin = fm;
                        }
                        if (typeof d.margin_level !== 'undefined') {
                            const ml = Number(d.margin_level);
                            if (!isNaN(ml)) snapshot.marginLevel = ml;
                        }
                        if (typeof d.cash_available !== 'undefined') {
                            const cash = Number(d.cash_available);
                            if (!isNaN(cash)) snapshot.cashAvailable = cash;
                        }
                        const rawFloatingPnl = d.floating_pnl ?? d.unrealized_pnl ?? d.total_unrealized_pnl;
                        if (typeof rawFloatingPnl !== 'undefined') {
                            const pnl = Number(rawFloatingPnl);
                            if (!isNaN(pnl)) snapshot.floatingPnl = pnl;
                        }
                        if (d.currency) snapshot.currency = d.currency;
                        if (Array.isArray(d.open_positions)) {
                            snapshot.openPositions = normalizePositions(d.open_positions);
                            hasOpenPositionsSnapshot = true;
                        }
                        if (Array.isArray(d.pending_orders)) snapshot.pendingOrders = d.pending_orders;
                    }

                    if (!hasOpenPositionsSnapshot && !detailsWarmingUp) {
                        try {
                            const v = await fetchOpenTrades(accountId);
                            // The endpoint returns { open_positions: [...] } or { results: [...] } or a plain array
                            const list = Array.isArray(v)
                                ? v
                                : Array.isArray(v?.open_positions)
                                    ? v.open_positions
                                    : Array.isArray(v?.results)
                                        ? v.results
                                        : null;
                            if (Array.isArray(list)) {
                                snapshot.openPositions = normalizePositions(list);
                            }
                        } catch (_) {}
                    }
                    cacheAndApplySnapshot(accountId, snapshot);
                } catch (e) {
                    console.error('Failed to fetch initial snapshot:', e);
                } finally {
                    snapshotRequestsRef.current.delete(accountId);
                }
            })();
            snapshotRequestsRef.current.set(accountId, request);
        }

        const authToken = localStorage.getItem("token");
        if (!authToken) {
            console.error("Authentication token not found for WebSocket.");
            setIsConnected(false);
            return;
        }

        const origin = WS_BASE_URL || (() => {
            const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
            return `${proto}://${window.location.host}`;
        })();

        const ws = new WebSocket(`${origin}/ws/accounts/${accountId}/?token=${authToken}`);
        socketsRef.current.set(accountId, ws);
        wsRef.current = ws;

        ws.onopen = () => {
            console.log("[open] Account data connection established");
            reconnectDelaysRef.current.set(accountId, ACCOUNT_WS_RECONNECT_INITIAL_MS);
            const existingTimer = heartbeatTimersRef.current.get(accountId);
            if (existingTimer) clearInterval(existingTimer);
            heartbeatTimersRef.current.set(accountId, setInterval(() => {
                try {
                    if (ws.readyState === WebSocket.OPEN) {
                        ws.send(JSON.stringify({ action: 'heartbeat', ts: Date.now() }));
                    }
                } catch (_) {}
            }, 60_000));
            if (selectedAccountRef.current === accountId) {
                setIsConnected(true);
            }
        };

        ws.onclose = (event) => {
            if (event.wasClean) {
                console.log(`[close] Account data connection closed cleanly, code=${event.code} reason=${event.reason}`);
            } else {
                console.log('[close] Account data connection died');
            }
            if (socketsRef.current.get(accountId) === ws) {
                socketsRef.current.delete(accountId);
            }
            const heartbeatTimer = heartbeatTimersRef.current.get(accountId);
            if (heartbeatTimer) {
                clearInterval(heartbeatTimer);
                heartbeatTimersRef.current.delete(accountId);
            }
            if (wsRef.current === ws) {
                wsRef.current = null;
            }
            if (selectedAccountRef.current === accountId) {
                setIsConnected(false);
            }
            const shouldReconnect = (
                selectedAccountRef.current === accountId &&
                ![4003, 4004].includes(event?.code)
            );
            if (shouldReconnect && localStorage.getItem("token")) {
                const delay = event?.code === 4000
                    ? 100
                    : (reconnectDelaysRef.current.get(accountId) || ACCOUNT_WS_RECONNECT_INITIAL_MS);
                clearReconnectTimer(accountId);
                reconnectTimersRef.current.set(accountId, setTimeout(() => {
                    reconnectTimersRef.current.delete(accountId);
                    if (selectedAccountRef.current !== accountId) return;
                    connect(accountId, { primeSnapshot: true, forcePrimeSnapshot: true, resetOnNoCache: false });
                }, delay));
                reconnectDelaysRef.current.set(
                    accountId,
                    Math.min(Math.max(delay, ACCOUNT_WS_RECONNECT_INITIAL_MS) * 1.5, ACCOUNT_WS_RECONNECT_MAX_MS)
                );
            }
        };

        ws.onerror = (error) => {
            console.error(`[error] Account data websocket error: ${error.message}`);
            if (selectedAccountRef.current === accountId) {
                setIsConnected(false);
            }
        };

        ws.onmessage = (event) => {
            //console.log("[message] Account data received:", event.data);
            const message = JSON.parse(event.data);

            if (message.error) {
                console.error(`[error] Server error: ${message.error}`);
                return;
            }

            if (message.type === 'account_update') {
                const data = message.data || {};
                const { balance, equity, margin, free_margin, cash_available, floating_pnl, unrealized_pnl, profit, currency, margin_level, open_positions, pending_orders } = data;
                const nextFloatingPnl = floating_pnl ?? unrealized_pnl ?? profit;
                const patch = {
                    ...(typeof balance === 'number' ? { balance } : {}),
                    ...(typeof equity === 'number' ? { equity } : {}),
                    ...(hasOwn(data, 'margin') && (typeof margin === 'number' || margin == null) ? { margin: margin ?? null } : {}),
                    ...(hasOwn(data, 'free_margin') && (typeof free_margin === 'number' || free_margin == null) ? { freeMargin: free_margin ?? null } : {}),
                    ...(hasOwn(data, 'cash_available') && (typeof cash_available === 'number' || cash_available == null) ? { cashAvailable: cash_available ?? null } : {}),
                    ...((hasOwn(data, 'floating_pnl') || hasOwn(data, 'unrealized_pnl') || hasOwn(data, 'profit')) && (typeof nextFloatingPnl === 'number' || nextFloatingPnl == null) ? { floatingPnl: nextFloatingPnl ?? null } : {}),
                    ...(currency ? { currency } : {}),
                    ...(hasOwn(data, 'margin_level') && (typeof margin_level === 'number' || margin_level == null) ? { marginLevel: margin_level ?? null } : {}),
                    ...(Array.isArray(open_positions) ? { openPositions: normalizePositions(open_positions) } : {}),
                    ...(Array.isArray(pending_orders) ? { pendingOrders: pending_orders } : {}),
                };
                cacheAndApplySnapshot(accountId, patch);
            }
        };
    }, [applySnapshot, cacheAndApplySnapshot, clearReconnectTimer, resetVisibleState]);

    const disconnect = useCallback(() => {
        // Invalidate any in-flight snapshot
        connectReqRef.current += 1;
        connectedAccountRef.current = null;
        selectedAccountRef.current = null;
        setConnectedAccountId(null);
        closeAllSockets();
        setIsConnected(false);
        console.log("[disconnect] Account data connection closed.");
    }, [closeAllSockets]);

    // Close WebSocket when user leaves the website, logs out, or provider unmounts
    useEffect(() => {
        window.addEventListener('beforeunload', closeAllSockets);
        // On logout, close socket AND clear stale account data from React state
        const resetState = () => {
            closeAllSockets();
            accountSnapshotsRef.current.clear();
            snapshotRequestsRef.current.clear();
            snapshotPrimeTimesRef.current.clear();
            setBalance(null);
            setEquity(null);
            setMargin(null);
            setFreeMargin(null);
            setMarginLevel(null);
            setCashAvailable(null);
            setFloatingPnl(null);
            setCurrency(null);
            setOpenPositions([]);
            setPendingOrders([]);
            setIsConnected(false);
            setConnectedAccountId(null);
        };
        window.addEventListener('valpha:logout', resetState);
        return () => {
            window.removeEventListener('beforeunload', closeAllSockets);
            window.removeEventListener('valpha:logout', resetState);
            // Provider unmounting (full app teardown) — clean up
            closeAllSockets();
        };
    }, [closeAllSockets]);

    const value = {
        connect,
        disconnect,
        isConnected,
        balance,
        equity,
        margin,
        freeMargin,
        marginLevel,
        cashAvailable,
        floatingPnl,
        currency,
        openPositions,
        pendingOrders,
        connectedAccountId,
        selectedAccountId: connectedAccountId,
    };

    return (
        <AccountDataContext.Provider value={value}>
            {children}
        </AccountDataContext.Provider>
    );
};
